import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosApi from "src/app/config";
import { getCurrentIndiaDate } from "src/lib/dateUtils";

const initialState = {
  data: [],
  isLoading: false,
  isError: false,
  isSuccess: false,
  error: null,

  columnFilterData: {},
  isColumnFilterLoading: false,
  isColumnFilterError: false,
  isColumnFilterSuccess: false,
  columnFilterError: null,

  slipMetaDataData: {},
  isSlipMetaDataLoading: false,
  isSlipMetaDataError: false,
  isSlipMetaDataSuccess: false,
  slipMetaDataError: null,

  tripStats: {},
  tripStatsLoading: false,
  tripStatsError: false,
  tripStatsSuccess: false,
  tripStatsErrorMessage: null,

  materials: [],
  materialsLoading: false,
  materialsError: false,
  materialsSuccess: false,
  materialsErrorMessage: null,

  // File upload states
  uploadedFiles: {
    invoiceLink: null,
    supportingDocsLinks: [],
  },
  isUploadingFiles: false,
  uploadFilesError: null,
  uploadFilesSuccess: false,
};

// GET /trips - Get trips with filters (NEW API)
export const getTripsData = createAsyncThunk(
  "trips/getTripsData",
  async (thunkAPI: any, { rejectWithValue, fulfillWithValue }) => {
    try {
      const { token, organizationId, page, per_page, filters, sorting } = thunkAPI;

      const perPage = parseInt(per_page || "10");
      const offset = ((page || 1) - 1) * perPage;
      
      // Build query params for GET request
      const params = new URLSearchParams({
        organizationId,
        offset: offset.toString(),
        limit: perPage.toString(),
      });

      if (sorting) {
        params.append("sort", sorting);
      }

      if (filters && filters.length > 0) {
        params.append("filters", JSON.stringify(filters));
      }

      // @ts-ignore
      const response = await axiosApi({ token, organizationId }).get(`/trips?${params.toString()}`);
      return fulfillWithValue(response.data);
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// GET /trips/metadata - Get trips metadata (NEW API)
export const getTripsMetadata = createAsyncThunk(
  "trips/getTripsMetadata",
  async (thunkAPI: any, { rejectWithValue, fulfillWithValue }) => {
    try {
      const { token, organizationId } = thunkAPI;
      
      if (!organizationId) {
        return rejectWithValue("Organization ID is required");
      }

      // @ts-ignore
      const response = await axiosApi({ token, organizationId }).get(`/trips/metadata`);
      return fulfillWithValue(response.data);
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// GET /organization/materials/frontend - Get materials for dropdown
export const getOrganizationMaterials = createAsyncThunk(
  "trips/getOrganizationMaterials",
  async (thunkAPI: any, { rejectWithValue, fulfillWithValue }) => {
    try {
      const { token, organizationId } = thunkAPI;

      if (!organizationId) {
        return rejectWithValue("Organization ID is required");
      }

      // @ts-ignore
      const response = await axiosApi({ token, organizationId }).get(`/organization/materials/frontend`);
      return fulfillWithValue(response.data);
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// POST /trips - Create trip (NEW API)
export const createTrip = createAsyncThunk(
  "trips/createTrip",
  async (thunkAPI: any, { rejectWithValue, fulfillWithValue }) => {
    try {
      const { token, formData } = thunkAPI;

      if (!formData.organizationId || !formData.vehicleNumber || !formData.netWeight) {
        return rejectWithValue("Required fields missing");
      }

      // @ts-ignore
      const response = await axiosApi({ token, organizationId: formData.organizationId }).post(`/trips`, {
        ...formData,
        createdByOrganizationUserId: formData.createdByOrganizationUserId,
        tripCreatedOn: formData.tripCreatedOn || getCurrentIndiaDate(),
      });

      return fulfillWithValue(response.data);
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// PUT /trips/:id - Update trip (NEW API)
export const updateTrip = createAsyncThunk(
  "trips/updateTrip",
  async (thunkAPI: any, { rejectWithValue, fulfillWithValue }) => {
    try {
      const { token, formData } = thunkAPI;

      if (!formData.id) {
        return rejectWithValue("Trip ID is required");
      }

      const tripId = formData.id;
      const updateData = { ...formData };
      delete updateData.id;

      // @ts-ignore
      const response = await axiosApi({ token, organizationId: formData.organizationId }).put(`/trips/${tripId}`, updateData);
      return fulfillWithValue(response.data);
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// PUT /trips/:tripId/verify - Verify trip (NEW BIFURCATED API)
export const verifyTrip = createAsyncThunk(
  "trips/verifyTrip",
  async (thunkAPI: any, { rejectWithValue, fulfillWithValue }) => {
    try {
      const { token, formData } = thunkAPI;

      if (!formData.id) {
        return rejectWithValue("Trip ID is required");
      }

      const tripId = formData.id;
      const updateData = { ...formData };
      delete updateData.id;

      // @ts-ignore
      const response = await axiosApi({ token, organizationId: formData.organizationId }).put(`/trips/${tripId}/verify`, updateData);
      return fulfillWithValue(response.data);
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// PUT /trips/:tripId/financials - Update trip financials (NEW BIFURCATED API)
export const updateTripFinancials = createAsyncThunk(
  "trips/updateTripFinancials",
  async (thunkAPI: any, { rejectWithValue, fulfillWithValue }) => {
    try {
      const { token, formData } = thunkAPI;

      if (!formData.id) {
        return rejectWithValue("Trip ID is required");
      }

      const tripId = formData.id;
      const updateData = { ...formData };
      delete updateData.id;

      // @ts-ignore
      const response = await axiosApi({ token, organizationId: formData.organizationId }).put(`/trips/${tripId}/financials`, updateData);
      return fulfillWithValue(response.data);
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// POST /trips/verify - Verify multiple trips (NEW API)
// export const verifyTrips = createAsyncThunk(
//   "trips/verifyTrips",
//   async (thunkAPI: any, { rejectWithValue, fulfillWithValue }) => {
//     try {
//       const { token, tripIds, organizationUserId, organizationId } = thunkAPI;

//       if (!tripIds || tripIds.length === 0) {
//         return rejectWithValue("Trip IDs are required");
//       }

//       // @ts-ignore
//       const response = await axiosApi({ token, organizationId }).post(`/trips/verify`, {
//         idList: tripIds,
//         organizationUserId,
//       });

//       return fulfillWithValue(response.data);
//     } catch (error: any) {
//       return rejectWithValue(error.response?.data || error.message);
//     }
//   }
// );

// POST /trips/ocr - Process OCR (NEW API)
export const fetchSlipMetaData = createAsyncThunk(
  "trips/fetchSlipMetaData",
  async (thunkAPI: any, { rejectWithValue, fulfillWithValue }) => {
    try {
      const { token, files, organizationId } = thunkAPI;

      const formData = new FormData();
      for (const file of files) {
        formData.append("file", file);
      }

      // @ts-ignore
      const response = await axiosApi({
        token,
        organizationId,
        contentType: "multipart/form-data",
      }).post(`/trips/ocr`, formData);

      return fulfillWithValue(response.data);
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// GET /trips/stats - Get trip statistics (NEW API)
export const getTripStats = createAsyncThunk(
  "trips/getTripStats",
  async (thunkAPI: any, { rejectWithValue, fulfillWithValue }) => {
    try {
      const { token, organizationId } = thunkAPI;

      // @ts-ignore
      const response = await axiosApi({ token, organizationId }).get(`/trips/stats`);
      return fulfillWithValue(response.data);
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);


// DELETE /trips/:id - Delete trip (if needed)
export const deleteTrip = createAsyncThunk(
  "trips/deleteTrip",
  async (thunkAPI: any, { rejectWithValue, fulfillWithValue }) => {
    try {
      const { token, tripId, organizationId } = thunkAPI;

      if (!tripId) {
        return rejectWithValue("Trip ID is required");
      }

      // @ts-ignore
      const response = await axiosApi({ token, organizationId }).delete(`/trips/${tripId}`);
      return fulfillWithValue(response.data);
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// POST /uploads/files - Upload files to S3 (NEW API)
export const uploadFiles = createAsyncThunk(
  "trips/uploadFiles",
  async (thunkAPI: any, { rejectWithValue, fulfillWithValue }) => {
    try {
      const { token, files, organizationId } = thunkAPI;

      if (!files || files.length === 0) {
        return rejectWithValue("No files provided");
      }

      if (files.length > 4) {
        return rejectWithValue("Maximum 4 files allowed");
      }

      const formData = new FormData();
      for (const file of files) {
        formData.append("file", file);
      }

      // @ts-ignore
      const response = await axiosApi({
        token,
        organizationId,
        contentType: "multipart/form-data",
      }).post(`/uploads/files`, formData);

      return fulfillWithValue(response.data);
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const tripSlice = createSlice({
  name: "trips",
  initialState,
  reducers: {
    clearState: () => initialState,
    clearSuccess: (state) => {
      state.isSuccess = false;
    },
    resetSlipMetadata: (state) => {
      state.slipMetaDataData = {};
      state.isSlipMetaDataSuccess = false;
      state.isSlipMetaDataError = false;
      state.slipMetaDataError = null;
      state.isSlipMetaDataLoading = false;
    },
    // Add action to reset only error states without clearing data
    resetErrors: (state) => {
      state.isError = false;
      state.error = null;
      state.isColumnFilterError = false;
      state.columnFilterError = null;
    },
    // Reset file upload states
    resetFileUploads: (state) => {
      state.uploadedFiles = {
        invoiceLink: null,
        supportingDocsLinks: [],
      };
      state.isUploadingFiles = false;
      state.uploadFilesError = null;
      state.uploadFilesSuccess = false;
    },
  },
  extraReducers: (builder) => {
    // Get Trips Data
    builder.addCase(getTripsData.pending, (state) => {
      state.isLoading = true;
      state.isError = false;
      state.error = null;
    });
    builder.addCase(getTripsData.fulfilled, (state, action) => {
      state.isLoading = false;
      state.isSuccess = true;
      state.data = action.payload?.data || action.payload;
      state.isError = false;
    });
    builder.addCase(getTripsData.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as any;
      state.isError = true;
    });

    // Get Trips Metadata
    builder.addCase(getTripsMetadata.pending, (state) => {
      state.isColumnFilterLoading = true;
      state.isColumnFilterError = false;
      state.columnFilterError = null;
    });
    builder.addCase(getTripsMetadata.fulfilled, (state, action) => {
      state.isColumnFilterLoading = false;
      state.isColumnFilterSuccess = true;
      state.columnFilterData = action.payload?.data || action.payload;
      state.isColumnFilterError = false;
    });
    builder.addCase(getTripsMetadata.rejected, (state, action) => {
      state.isColumnFilterLoading = false;
      state.columnFilterError = action.payload as any;
      state.isColumnFilterError = true;
    });

    // Create Trip
    builder.addCase(createTrip.pending, (state) => {
      state.isLoading = true;
      state.isError = false;
    });
    builder.addCase(createTrip.fulfilled, (state, action) => {
      state.isLoading = false;
      state.isSuccess = true;
      state.isError = false;
    });
    builder.addCase(createTrip.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as any;
      state.isError = true;
    });

    // Update Trip
    builder.addCase(updateTrip.pending, (state) => {
      state.isLoading = true;
      state.isError = false;
    });
    builder.addCase(updateTrip.fulfilled, (state, action) => {
      state.isLoading = false;
      state.isSuccess = true;
      state.isError = false;
    });
    builder.addCase(updateTrip.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as any;
      state.isError = true;
    });

    // Verify Trip (NEW BIFURCATED API)
    builder.addCase(verifyTrip.pending, (state) => {
      state.isLoading = true;
      state.isError = false;
    });
    builder.addCase(verifyTrip.fulfilled, (state, action) => {
      state.isLoading = false;
      state.isSuccess = true;
      state.isError = false;
    });
    builder.addCase(verifyTrip.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as any;
      state.isError = true;
    });

    // Update Trip Financials (NEW BIFURCATED API)
    builder.addCase(updateTripFinancials.pending, (state) => {
      state.isLoading = true;
      state.isError = false;
    });
    builder.addCase(updateTripFinancials.fulfilled, (state, action) => {
      state.isLoading = false;
      state.isSuccess = true;
      state.isError = false;
    });
    builder.addCase(updateTripFinancials.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as any;
      state.isError = true;
    });

    // Verify Trips
    // builder.addCase(verifyTrips.pending, (state) => {
    //   state.isLoading = true;
    //   state.isError = false;
    // });
    // builder.addCase(verifyTrips.fulfilled, (state, action) => {
    //   state.isLoading = false;
    //   state.isSuccess = true;
    //   state.isError = false;
    // });
    // builder.addCase(verifyTrips.rejected, (state, action) => {
    //   state.isLoading = false;
    //   state.error = action.payload as any;
    //   state.isError = true;
    // });

    // Fetch Slip Metadata (OCR)
    builder.addCase(fetchSlipMetaData.pending, (state) => {
      state.isSlipMetaDataLoading = true;
      state.isSlipMetaDataError = false;
    });
    builder.addCase(fetchSlipMetaData.fulfilled, (state, action) => {
      state.isSlipMetaDataLoading = false;
      state.isSlipMetaDataSuccess = true;
      state.slipMetaDataData = action.payload?.data || action.payload;
      state.isSlipMetaDataError = false;
    });
    builder.addCase(fetchSlipMetaData.rejected, (state, action) => {
      state.isSlipMetaDataLoading = false;
      state.slipMetaDataError = action.payload as any;
      state.isSlipMetaDataError = true;
    });

    // Get Trip Stats
    builder.addCase(getTripStats.pending, (state) => {
      state.tripStatsLoading = true;
      state.tripStatsError = false;
    });
    builder.addCase(getTripStats.fulfilled, (state, action) => {
      state.tripStatsLoading = false;
      state.tripStatsSuccess = true;
      state.tripStats = action.payload?.data || action.payload;
      state.tripStatsError = false;
    });
    builder.addCase(getTripStats.rejected, (state, action) => {
      state.tripStatsLoading = false;
      state.tripStatsErrorMessage = action.payload as any;
      state.tripStatsError = true;
    });


    // Get Organization Materials
    builder.addCase(getOrganizationMaterials.pending, (state) => {
      state.materialsLoading = true;
      state.materialsError = false;
    });
    builder.addCase(getOrganizationMaterials.fulfilled, (state, action) => {
      state.materialsLoading = false;
      state.materialsSuccess = true;
      state.materials = action.payload?.data || [];
      state.materialsError = false;
    });
    builder.addCase(getOrganizationMaterials.rejected, (state, action) => {
      state.materialsLoading = false;
      state.materialsErrorMessage = action.payload as any;
      state.materialsError = true;
    });

    // Delete Trip
    builder.addCase(deleteTrip.pending, (state) => {
      state.isLoading = true;
      state.isError = false;
    });
    builder.addCase(deleteTrip.fulfilled, (state, action) => {
      state.isLoading = false;
      state.isSuccess = true;
      state.isError = false;
    });
    builder.addCase(deleteTrip.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as any;
      state.isError = true;
    });

    // Upload Files
    builder.addCase(uploadFiles.pending, (state) => {
      state.isUploadingFiles = true;
      state.uploadFilesError = null;
      state.uploadFilesSuccess = false;
    });
    builder.addCase(uploadFiles.fulfilled, (state, action) => {
      state.isUploadingFiles = false;
      state.uploadFilesSuccess = true;
      // Store the S3 links returned from the API
      const s3Links = action.payload?.data?.s3Links || [];
      // Determine if this is invoice or supporting docs based on count
      // Single file = invoice, multiple = supporting docs
      if (s3Links.length === 1) {
        state.uploadedFiles.invoiceLink = s3Links[0];
      } else {
        state.uploadedFiles.supportingDocsLinks = s3Links;
      }
    });
    builder.addCase(uploadFiles.rejected, (state, action) => {
      state.isUploadingFiles = false;
      state.uploadFilesError = action.payload as any;
      state.uploadFilesSuccess = false;
    });
  },
});

export const { clearState, clearSuccess, resetSlipMetadata, resetErrors, resetFileUploads } = tripSlice.actions;
export default tripSlice.reducer;